import { Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { isLoggedIn, getCachedUser, clearAuth, setCachedUser } from './lib/auth'
import { api } from './lib/api'
import type { User } from './lib/types'
import Layout from './components/Layout'
import Login from './pages/Login'
import Home from './pages/Home'
import Companies from './pages/Companies'
import CompanyPage from './pages/CompanyPage'
import Profile from './pages/Profile'
import Admin from './pages/Admin'
import Tracks from './pages/Tracks'
import IndustryMap from './pages/IndustryMap'
import FloorView from './pages/FloorView'
import PendingApproval from './pages/PendingApproval'
import PrivacyPage from './pages/PrivacyPage'

export default function App() {
  const [user, setUser] = useState<User | null>(getCachedUser())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isLoggedIn()) {
      api.me().then(u => { setUser(u); setCachedUser(u) })
        .catch(() => { clearAuth(); setUser(null) })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  const logout = () => { clearAuth(); setUser(null) }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    )
  }

  const needsApproval = user && !user.approved

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login onLogin={(u) => setUser(u)} />} />
      <Route element={<Layout user={user} onLogout={logout} />}>
        <Route path="/" element={<Home />} />
        <Route path="/companies" element={<Companies />} />
        <Route path="/industry-map" element={<IndustryMap />} />
        <Route path="/floor" element={<FloorView />} />
        <Route path="/companies/:id" element={
          needsApproval ? <PendingApproval /> : <CompanyPage user={user} />
        } />
        <Route path="/tracks" element={
          needsApproval ? <PendingApproval /> : <Tracks user={user} />
        } />
        <Route path="/profile" element={user ? <Profile user={user} onUpdate={setUser} /> : <Navigate to="/login" />} />
        <Route path="/admin" element={
          user?.role === 'admin' ? <Admin /> : <Navigate to="/" />
        } />
        <Route path="/pending" element={<PendingApproval />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Route>
    </Routes>
  )
}
